import * as Haptics from 'expo-haptics';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Circle, Path, Ellipse } from 'react-native-svg';

const NAVY = '#1E3A8A';
const GOLD = '#C9A843';

type Props = {
  visible: boolean;
  onDone: () => void;
};

function triggerHaptic() {
  void Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
}

function ConfettiPiece({ x, color, delay }: { x: number; color: string; delay: number }) {
  const translateY = useSharedValue(0);
  const opacity = useSharedValue(0);
  const rotate = useSharedValue(0);

  useEffect(() => {
    translateY.value = withDelay(delay, withTiming(-120, { duration: 700, easing: Easing.out(Easing.quad) }));
    opacity.value = withDelay(delay, withSequence(
      withTiming(1, { duration: 100 }),
      withDelay(400, withTiming(0, { duration: 300 })),
    ));
    rotate.value = withDelay(delay, withTiming(360, { duration: 700 }));
  }, []);

  const style = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }, { rotate: `${rotate.value}deg` }],
    opacity: opacity.value,
  }));

  return (
    <Animated.View style={[{ position: 'absolute', bottom: 40, left: x, width: 8, height: 8, borderRadius: 2, backgroundColor: color }, style]} />
  );
}

function HandshakeSvg({ progress }: { progress: number }) {
  // Two hands meeting from left and right
  const leftX = -60 + progress * 60;
  const rightX = 60 - progress * 60;

  return (
    <Svg width={200} height={120} viewBox="0 0 200 120">
      {/* Left hand */}
      <Path
        d={`M ${50 + leftX} 70 C ${55 + leftX} 50, ${70 + leftX} 45, ${80 + leftX} 55 L ${90 + leftX} 48 C ${93 + leftX} 44, ${100 + leftX} 46, ${98 + leftX} 52 L ${95 + leftX} 56 C ${98 + leftX} 53, ${104 + leftX} 55, ${102 + leftX} 61 L ${99 + leftX} 64 C ${102 + leftX} 62, ${107 + leftX} 64, ${105 + leftX} 70 L ${100 + leftX} 80 C ${90 + leftX} 90, ${70 + leftX} 88, ${60 + leftX} 80 Z`}
        fill={NAVY}
      />
      {/* Left arm */}
      <Path
        d={`M ${20 + leftX} 90 L ${60 + leftX} 70 L ${70 + leftX} 85 L ${30 + leftX} 105 Z`}
        fill={NAVY}
      />
      {/* Right hand */}
      <Path
        d={`M ${150 + rightX} 70 C ${145 + rightX} 50, ${130 + rightX} 45, ${120 + rightX} 55 L ${110 + rightX} 48 C ${107 + rightX} 44, ${100 + rightX} 46, ${102 + rightX} 52 L ${105 + rightX} 56 C ${102 + rightX} 53, ${96 + rightX} 55, ${98 + rightX} 61 L ${101 + rightX} 64 C ${98 + rightX} 62, ${93 + rightX} 64, ${95 + rightX} 70 L ${100 + rightX} 80 C ${110 + rightX} 90, ${130 + rightX} 88, ${140 + rightX} 80 Z`}
        fill={GOLD}
      />
      {/* Right arm */}
      <Path
        d={`M ${180 + rightX} 90 L ${140 + rightX} 70 L ${130 + rightX} 85 L ${170 + rightX} 105 Z`}
        fill={GOLD}
      />
      {/* Shake lines — visible only when hands meet */}
      {progress > 0.85 && (
        <>
          <Path d="M 90 35 L 85 25" stroke={GOLD} strokeWidth="2.5" strokeLinecap="round" />
          <Path d="M 100 30 L 100 18" stroke={NAVY} strokeWidth="2.5" strokeLinecap="round" />
          <Path d="M 110 35 L 115 25" stroke={GOLD} strokeWidth="2.5" strokeLinecap="round" />
          <Circle cx="100" cy="62" r="6" fill="rgba(201,168,67,0.25)" />
        </>
      )}
    </Svg>
  );
}

export function HandshakeAnimation({ visible, onDone }: Props) {
  const { t } = useTranslation();
  const opacity = useSharedValue(0);
  const scale = useSharedValue(0.7);
  const handProgress = useSharedValue(0);
  const celebrateScale = useSharedValue(1);

  useEffect(() => {
    if (!visible) {
      opacity.value = 0;
      scale.value = 0.7;
      handProgress.value = 0;
      celebrateScale.value = 1;
      return;
    }

    runOnJS(triggerHaptic)();

    opacity.value = withTiming(1, { duration: 250 });
    scale.value = withSpring(1, { damping: 14, stiffness: 200 });

    handProgress.value = withDelay(300, withTiming(1, { duration: 600, easing: Easing.out(Easing.cubic) }));

    celebrateScale.value = withDelay(950, withSequence(
      withSpring(1.12, { damping: 8, stiffness: 300 }),
      withSpring(1, { damping: 12, stiffness: 200 }),
    ));

    const timer = setTimeout(() => {
      opacity.value = withTiming(0, { duration: 350 }, (finished) => {
        if (finished) runOnJS(onDone)();
      });
      scale.value = withTiming(0.85, { duration: 350 });
    }, 2200);

    return () => clearTimeout(timer);
  }, [visible]);

  const overlayStyle = useAnimatedStyle(() => ({ opacity: opacity.value }));
  const cardStyle = useAnimatedStyle(() => ({ transform: [{ scale: scale.value }] }));
  const progressNum = handProgress.value;

  if (!visible) return null;

  const confettiColors = [GOLD, NAVY, '#F97316', '#10B981', GOLD, NAVY];
  const confettiXs = [40, 60, 80, 100, 120, 140];

  return (
    <Animated.View style={[StyleSheet.absoluteFillObject, styles.overlay, overlayStyle]} pointerEvents="none">
      <Animated.View style={[styles.card, cardStyle]}>
        <View style={styles.svgWrap}>
          <HandshakeSvg progress={progressNum} />
          {confettiColors.map((color, i) => (
            <ConfettiPiece key={i} x={confettiXs[i]} color={color} delay={900 + i * 60} />
          ))}
        </View>
        <Text style={styles.title}>{t('agreements.completedTitle')}</Text>
        <Text style={styles.sub}>{t('agreements.completedSub')}</Text>
      </Animated.View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    backgroundColor: 'rgba(0,0,0,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 20,
    paddingHorizontal: 32,
    paddingVertical: 28,
    alignItems: 'center',
    width: 300,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 20,
    elevation: 20,
  },
  svgWrap: {
    position: 'relative',
    width: 200,
    height: 120,
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: NAVY,
    textAlign: 'center',
    marginBottom: 6,
  },
  sub: {
    fontSize: 13,
    color: '#666',
    textAlign: 'center',
    lineHeight: 18,
  },
});
